import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Spreadsheet-style multi-select for the staff calendar. Lets a manager pick
// many slots at once (click, or shift-click for a range) and assign one or more
// employees to all of them in a single action — the at-scale alternative to
// dragging employees one slot at a time. Also serves as the keyboard/non-drag
// path to assignment.
//
// Attached to the calendar root. Selectable slots are the existing
// `.calendar-dropzone` elements, whose data-* attributes already describe the
// slot (tour: schedule/date/time, or assignable: type/id).
export default class extends Controller {
  static targets = ["bar", "count", "slotInputs", "modal", "unassignModal"]
  // availabilityOnly: limited-user availability mode — only tour slots are
  // selectable. Widen isSelectable() to extend availability to other slot types.
  // dayOnly: day-assignment mode — only whole-day zones are selectable, never
  // the legacy per-slot dropzones that may still hold old timeslot pills.
  static values = { availabilityOnly: Boolean, dayOnly: Boolean }

  connect() {
    this.selected = new Set()
    this.lastIndex = null
    this.onClick = this.onClick.bind(this)
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onStreamRender = this.onStreamRender.bind(this)
    this.element.addEventListener("pointerdown", this.onPointerDown)
    this.element.addEventListener("click", this.onClick)
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.onPointerDown)
    this.element.removeEventListener("click", this.onClick)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
    this.element.classList.remove("has-selection")
  }

  onPointerDown(event) {
    this.downX = event.clientX
    this.downY = event.clientY
  }

  // Distinguishes a real tap from the tail end of a drag (SortableJS reorder,
  // scroll, or a drag-and-drop assign) — those fire a synthetic click on the
  // nearest common ancestor, which would otherwise toggle selection.
  dragged(event) {
    return this.downX != null &&
      (Math.abs(event.clientX - this.downX) > 8 || Math.abs(event.clientY - this.downY) > 8)
  }

  onKeydown(event) {
    if (event.key !== "Escape") return
    if (document.querySelector(".modal.show")) return
    if (this.selected.size) this.clearSelection()
  }

  onStreamRender() {
    requestAnimationFrame(() => {
      let changed = false
      this.selected.forEach((zone) => {
        if (!zone.isConnected) {
          this.selected.delete(zone)
          changed = true
        }
      })
      if (changed) {
        this.lastIndex = null
        this.updateBar()
      }
    })
  }

  onClick(event) {
    if (this.dragged(event)) return
    if (event.target.closest("button, a, .employee-avatar-pill")) return

    // Day mode: the day zone may be collapsed entirely (no pills), so the
    // whole day cell/card is the tap target — a tap anywhere on it toggles the
    // day, except on real controls (status chip link, day-select-all, etc.).
    if (this.dayOnlyValue) {
      const day = event.target.closest(".sched-day, .calendar-day")
      if (day && this.element.contains(day)) {
        const zone = day.querySelector('.calendar-dropzone[data-all-day]')
        if (zone) {
          event.preventDefault()
          this.toggleZone(zone, event.shiftKey)
        }
        return
      }
    }

    // Mobile week view: the dropzone is a small strip inside an event card, so
    // treat a tap anywhere on the card (.sched-row) as selecting that row's
    // single slot.
    const row = event.target.closest(".sched-row")
    if (row && this.element.contains(row)) {
      const zone = row.querySelector(".calendar-dropzone")
      if (zone) {
        event.preventDefault()
        this.toggleZone(zone, event.shiftKey)
      }
      return
    }

    // Month-grid slot cards: the dropzone collapses when it holds no pills, so
    // a click anywhere on the slot card selects its slot.
    const slot = event.target.closest(".calendar-tour-slot")
    if (slot && this.element.contains(slot)) {
      const zone = slot.querySelector(".calendar-dropzone")
      if (zone) {
        event.preventDefault()
        this.toggleZone(zone, event.shiftKey)
      }
      return
    }

    const zone = event.target.closest(".calendar-dropzone")
    if (!zone || !this.element.contains(zone)) return

    event.preventDefault()
    this.toggleZone(zone, event.shiftKey)
  }

  toggleZone(zone, withShift) {
    const zones = this.zones()
    const index = zones.indexOf(zone)

    if (withShift && this.lastIndex !== null) {
      const [from, to] = [this.lastIndex, index].sort((a, b) => a - b)
      for (let i = from; i <= to; i++) this.select(zones[i])
    } else if (this.selected.has(zone)) {
      this.deselect(zone)
    } else {
      this.select(zone)
    }

    this.lastIndex = index
    this.updateBar()
  }

  // Select (or, if all already selected, deselect) every selectable slot within
  // the clicked day — month-grid cell or mobile agenda day.
  selectDay(event) {
    event.preventDefault()

    const day = event.target.closest(".calendar-day, .sched-day")
    if (!day) return

    const zones = Array.from(day.querySelectorAll(".calendar-dropzone")).filter((z) => this.isSelectable(z))
    if (zones.length === 0) return

    const allSelected = zones.every((z) => this.selected.has(z))
    zones.forEach((z) => (allSelected ? this.deselect(z) : this.select(z)))
    this.lastIndex = null
    this.updateBar()
  }

  select(zone) {
    if (!zone || !this.isSelectable(zone)) return
    this.selected.add(zone)
    zone.classList.add("slot-selected")
  }

  // In availability mode only tour slots can be picked; in day mode only day
  // zones; managers in timeslot mode can pick any.
  isSelectable(zone) {
    const token = this.tokenFor(zone)
    if (token == null) return false
    if (this.availabilityOnlyValue) return token.startsWith("tour:")
    if (this.dayOnlyValue) return token.startsWith("day:")
    return true
  }

  deselect(zone) {
    this.selected.delete(zone)
    zone.classList.remove("slot-selected")
  }

  clearSelection() {
    this.selected.forEach((zone) => zone.classList.remove("slot-selected"))
    this.selected.clear()
    this.lastIndex = null
    this.updateBar()
  }

  clear() {
    this.clearSelection()
  }

  // Mirror the current selection into every form's hidden slots[] inputs (the
  // assign modal, the unassign modal, and the limited-user availability bar each
  // carry their own slotInputs container). Kept in sync on every selection
  // change so whichever submit path fires always sends the picked slots.
  syncSlotInputs() {
    if (!this.hasSlotInputsTarget) return

    this.slotInputsTargets.forEach((container) => {
      container.innerHTML = ""
      this.selected.forEach((zone) => {
        const token = this.tokenFor(zone)
        if (!token) return
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "slots[]"
        input.value = token
        container.appendChild(input)
      })
    })
  }

  // URLSearchParams of the picked slot tokens, used to lazy-load each modal's
  // employee list frame.
  slotParams() {
    const params = new URLSearchParams()
    this.selected.forEach((zone) => {
      const token = this.tokenFor(zone)
      if (token) params.append("slots[]", token)
    })
    return params
  }

  // Wire the picked slots into the assign form, then open the employee picker.
  openAssign() {
    if (this.selected.size === 0 || !this.hasModalTarget) return

    this.syncSlotInputs()
    const frame = this.modalTarget.querySelector("#selected_slots_employees")
    if (frame) {
      frame.setAttribute("src", `/companies/assignments/selected_slots_employees?${this.slotParams().toString()}`)
    }

    this.modalInstance().show()
  }

  // Wire the picked slots into the unassign form, then open the picker listing
  // the employees currently assigned to those slots.
  openUnassign() {
    if (this.selected.size === 0 || !this.hasUnassignModalTarget) return

    this.syncSlotInputs()
    const frame = this.unassignModalTarget.querySelector("#selected_slots_assigned_employees")
    if (frame) {
      frame.setAttribute("src", `/companies/assignments/selected_slots_assigned_employees?${this.slotParams().toString()}`)
    }

    this.unassignModalInstance().show()
  }

  // Called on the assign/unassign/availability form's turbo:submit-end. Closes
  // whichever modal is open (no-op when none is) and clears the selection.
  onAssigned(event) {
    if (!event.detail.success) return
    if (this.hasModalTarget) this.modalInstance().hide()
    if (this.hasUnassignModalTarget) this.unassignModalInstance().hide()
    this.clearSelection()
  }

  tokenFor(zone) {
    const d = zone.dataset
    if (d.allDay) return `day:${d.date}`
    if (d.scheduleId) return `tour:${d.scheduleId}:${d.date}:${d.time}`
    if (d.assignableId && d.assignableType) return `assignable:${d.assignableType}:${d.assignableId}`
    return null
  }

  zones() {
    return Array.from(this.element.querySelectorAll(".calendar-dropzone")).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0
    )
  }

  updateBar() {
    const count = this.selected.size
    this.element.classList.toggle("has-selection", count > 0)
    if (this.hasCountTarget) this.countTarget.textContent = count
    if (this.hasBarTarget) this.barTarget.classList.toggle("d-none", count === 0)

    this.syncSlotInputs()
    this.updateDayButtons()
  }

  // Keeps each day's "select all" checkbox lit up exactly when clicking it
  // would deselect (i.e. every one of its zones is already selected) — matches
  // selectDay's own all-or-nothing toggle, so the lit state never promises a
  // click will do the opposite of what it shows. The mobile day view relies on
  // this button as the only visible way to select a day.
  updateDayButtons() {
    this.element.querySelectorAll(".day-select-all").forEach((btn) => {
      const day = btn.closest(".calendar-day, .sched-day")
      if (!day) return
      const zones = Array.from(day.querySelectorAll(".calendar-dropzone")).filter((z) => this.isSelectable(z))
      const active = zones.length > 0 && zones.every((z) => this.selected.has(z))
      btn.classList.toggle("active", active)
      btn.classList.toggle("btn-primary", active)
      btn.classList.toggle("btn-outline-primary", !active)
    })
  }

  modalInstance() {
    this._modal ||= Modal.getOrCreateInstance(this.modalTarget)
    return this._modal
  }

  unassignModalInstance() {
    this._unassignModal ||= Modal.getOrCreateInstance(this.unassignModalTarget)
    return this._unassignModal
  }
}
